import { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Alert, FlexColumn, TextField } from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { useSlackIntegration } from 'shared/hooks/integrations';
import TestMessageSection from './TestMessageSection';

type Props = {
  channels?: string[];
  onSuccess?: () => void;
  onReset?: () => void;
};

const SlackTestSection = ({
  channels: propChannels,
  onSuccess,
  onReset,
}: Props) => {
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');
  const [channelsFallback, setChannelsFallback] = useState(false);
  const [hasFetchedChannels, setHasFetchedChannels] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const hasInitialized = useRef(false);

  const { openSnackbar } = useSnackbar();
  const selectedEnvId = useSelectedEnv();

  const { slackChannelsMutation, slackQuery } = useSlackIntegration({
    envId: selectedEnvId,
  });

  const isSlackIntegrationReady =
    slackQuery.data?.integration || slackQuery.data?.defaultDestination;

  const defaultChannelName =
    slackQuery.data?.defaultDestination?.channelName ?? '';

  const channels = useMemo(
    () => propChannels || slackChannelsMutation.data?.channels || [],
    [propChannels, slackChannelsMutation.data],
  );

  const defaultMessage =
    'This is a test message from Elementary Data to verify your Slack integration is working correctly.';

  const sortedChannels = useMemo(() => [...channels].sort(), [channels]);

  // Pre-fill with the integration's default channel once on first load.
  useEffect(() => {
    if (!hasInitialized.current && defaultChannelName) {
      hasInitialized.current = true;
      setSelectedChannel(defaultChannelName);
    }
  }, [defaultChannelName]);

  const testMutation = useMutation(
    (data: { channel_name: string; message?: string }) =>
      api.sendSlackTestMessage(data),
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test message sent successfully to Slack!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message: response.message || 'Failed to send test message to Slack',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test message to Slack',
          variant: 'error',
        });
      },
    },
  );

  useEffect(() => {
    if (!isSlackIntegrationReady) return;
    if (hasFetchedChannels) return;
    if (!selectedEnvId) return;
    if (propChannels) return;

    setHasFetchedChannels(true);
    slackChannelsMutation.mutate();

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(() => {
      setChannelsFallback(true);
    }, 10000);
  }, [
    selectedEnvId,
    isSlackIntegrationReady,
    slackChannelsMutation,
    hasFetchedChannels,
    propChannels,
  ]);

  // Clear timeout once channels arrive
  useEffect(() => {
    if (channels.length > 0 && fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
  }, [channels]);

  const effectiveChannel = selectedChannel.trim();

  const handleSendTest = () => {
    if (!effectiveChannel) return;

    const message = customMessage.trim() || defaultMessage;
    testMutation.mutate({
      channel_name: effectiveChannel,
      message,
    });
  };

  const integrationErrorMessage = !isSlackIntegrationReady
    ? 'Slack integration needs to be properly set up before fetching channels. Please configure your Slack integration first.'
    : null;

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test message to verify your Slack integration is working
        correctly.
      </Typography>

      {integrationErrorMessage && (
        <Alert severity="error">{integrationErrorMessage}</Alert>
      )}

      {!!slackChannelsMutation.error && !propChannels && (
        <Alert severity="error" data-testid="channels-error">
          Failed to load available channels. Please try again.
        </Alert>
      )}

      {channelsFallback && !propChannels && (
        <Alert severity="info" data-testid="channels-timeout-info">
          Channel loading timed out. You can still type your channel name
          manually (e.g., #general).
        </Alert>
      )}

      <FormControls.InputWrapper>
        <Autocomplete
          options={sortedChannels}
          freeSolo
          value={selectedChannel}
          onSelect={(e: any) => setSelectedChannel(e.target.value)}
          onChange={(_, newValue) => {
            if (newValue) {
              setSelectedChannel(
                typeof newValue === 'string' ? newValue : newValue || '',
              );
            }
          }}
          loading={
            slackChannelsMutation.isLoading &&
            !channelsFallback &&
            !propChannels
          }
          disabled={testMutation.isLoading || !isSlackIntegrationReady}
          disablePortal
          ListboxProps={{
            sx: { maxHeight: '240px' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Slack channel"
              placeholder="e.g., #general"
              data-testid="channel-input"
              size="large"
            />
          )}
        />
      </FormControls.InputWrapper>

      {isSlackIntegrationReady &&
        !slackChannelsMutation.isLoading &&
        !channels.length &&
        !channelsFallback &&
        !propChannels && (
          <Alert severity="info" data-testid="no-channels-info">
            No channels loaded. You can still type your channel name manually
            (e.g., #general).
          </Alert>
        )}

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        disabled={!selectedChannel}
      />
    </FlexColumn>
  );
};

export default SlackTestSection;
